// ============================================
// STORAGE.JS — Sistema centralizado de localStorage
// ============================================
// UNICA fuente de verdad para todas las claves del proyecto.
// Si necesitas usar localStorage, importa de aqui. NO uses strings literales.
//
// Razon: cuando se hacen cambios al sistema, esto evita que un fix accidental
// rompa otra parte. Si quieres ver TODAS las claves que usa el proyecto,
// este archivo es el catalogo completo (auditado en REFACTOR-STORAGE).
//
// Estado: claves del flujo critico (auth + PWA + dashboard) ya migradas a
// usar STORAGE_KEYS desde aqui. Claves de modulos secundarios (mocks,
// drafts, cache local) siguen como literales en sus archivos pero estan
// listadas abajo para refactorizacion futura.

/**
 * Catalogo completo de claves de localStorage del proyecto.
 * Agrupadas por tema para mantenimiento facil.
 */
export const STORAGE_KEYS = {
  // ========== AUTH (core) ==========
  // Token JWT del usuario logueado. Se borra en logout.
  AUTH_TOKEN: 'beep_token',
  // Snapshot del usuario logueado (id, correo, nombre, rol, ...).
  USER_DATA: 'beep_usuario',
  // ========== VER-COMO (impersonacion direccion) ==========
  // Respaldo del token + usuario ORIGINALES del director mientras dura el modo
  // "Ver como". Viven en localStorage (mismo perfil de seguridad que AUTH_TOKEN,
  // que ya vive ahi) para sobrevivir recargas y pestanas nuevas: el director
  // JAMAS queda atrapado en la vista si recarga la pagina.
  VER_COMO_BACKUP: 'beep_ver_como_backup',
  // Metadatos del modo activo: { objetivo, actor, expira } (epoch ms). Su
  // presencia es lo que enciende el banner ambar.
  VER_COMO_META: 'beep_ver_como_meta',
  // Permisos editados localmente (Usuarios.js admin). LEGACY: el backend es la verdad.
  USER_PERMISOS: 'beep_permisos',
  // Cache de usuario en context (con guion). Distinto de USER_DATA por
  // razones historicas. UsuarioContext.js es su unico consumidor.
  USER_CONTEXT_CACHE: 'beep-usuario',

  // ========== APP ROUTING / PWA ==========
  // Que app esta activa: 'beep' | 'expo'.
  // CRITICO para PWA: al cambiar, App.js usa esto como key del Dashboard
  // para forzar remount y evitar componentes con state obsoleto.
  CURRENT_APP: 'beep_app',
  // Pestaña/seccion activa del Dashboard de Beep (ej: 'cotizaciones.tienda').
  DASHBOARD_ACTIVE_TAB: 'beep_dashboard_activo',
  // Sidebar colapsado en desktop: 'colapsado' | 'expandido'.
  DASHBOARD_SIDEBAR: 'beep_sidebar',
  // ALERTA-BAJO-COSTO: lista a abrir al llegar a Listas pactadas / Colecciones desde el aviso
  // del vigilante ({ id, nombre }). Se lee y se borra al montar la pantalla destino.
  LISTA_ABRIR: 'beep_lista_abrir',
  // ========== DIAGNOSTICO ==========
  // Flag '1'/'0' que enciende el diagnostico EN VIVO de saltos de scroll
  // (utils/diagScroll.js). Se prende tocando 5 veces la version en el menu de perfil.
  DIAG_SCROLL: 'beep_diag_scroll',

  // ========== TEMA / UI ==========
  // Tema activo: 'claro' | 'oscuro' (utils/tema.js).
  TEMA: 'beep-tema',
  // Modal "instalar PWA" descartado: timestamp ms del descarte.
  INSTALL_PROMPT_DISMISS: 'beep-install-dismiss',

  // ========== TIENDA / PREFERENCIAS ==========
  // Orden activo del catalogo en La Tienda.
  TIENDA_ORDEN: 'beep_orden_tienda',
  // MOTOR DE ORIGEN F3.B: "modo agregar a cotizacion" (independiente del carrito).
  // { cotizacionId, nombre } | ausente. Activo mientras se eligen productos para una
  // cotizacion en EDITAR; lo leen La Tienda/Colecciones/Listas para mostrar la barra
  // y ocultar el carrito.
  MODO_AGREGAR_COTIZACION: 'beep_modo_agregar_cotizacion',

  // ========== EXPEDIENTES ==========
  // Filtros activos de la pantalla Expedientes (pestana, estadoFunnel,
  // responsableId, clienteGrupoId, necesidadFiltro, etc.). Tambien es el
  // canal por el que MI-DIA-V2 (Mi Bandeja) pre-aplica filtros antes de
  // navegar a /expedientes.
  EXPEDIENTES_FILTROS: 'beep_expedientes_filtros',
  // Vista recordada de Expedientes: 'kanban' | 'tabla' (default de fabrica: tabla).
  // LEGADA desde EXPEDIENTES-RESUMEN (2026-09-17): era GLOBAL, asi que en un equipo compartido la
  // vista de uno se le pegaba al siguiente. Se sustituye por EXPEDIENTES_VISTA (por usuario); esta
  // solo se LEE una vez, para que nadie pierda su preferencia al estrenar la version nueva.
  CONTROL_EXP_VISTA: 'beep_control_exp_vista',
  // EXPEDIENTES-RESUMEN (2026-09-17): vista recordada POR USUARIO de la pantalla Expedientes.
  // Un solo objeto { "<usuarioId>": 'resumen' | 'tabla' | 'kanban' }. De fabrica: direccion abre
  // en Resumen, el resto en Tabla.
  EXPEDIENTES_VISTA: 'beep_expedientes_vista',
  // EXPEDIENTES-RESUMEN (2026-09-17): vistas guardadas (combinaciones de filtros con nombre), POR
  // USUARIO. Un solo objeto { "<usuarioId>": [ { id, nombre, filtros } ] }. Ver utils/vistasGuardadas.js.
  EXPEDIENTES_VISTAS: 'beep_expedientes_vistas',
  // DEPRECATED (TRANSPARENCIA-TOTAL 2026-07-09): el switch "Ver todos" de Expedientes
  // se elimino al fusionar la vista. Clave conservada solo para no invalidar storage
  // viejo; ya nadie la lee ni escribe. Retirar en una limpieza futura.
  CONTROL_EXP_VER_TODOS: 'beep_control_exp_ver_todos',
  // Switch "Ver todos" en la bandeja de Mi Trabajo (direccion): '1' = todos los expedientes.
  MITRABAJO_VER_TODOS: 'beep_mitrabajo_ver_todos',

  // ========== DRAFTS / TRABAJO EN CURSO ==========
  // Borrador de levantamiento (Levantamiento.js).
  LEVANTAMIENTO_DRAFT: 'beep-lev-draft',
  // MOVIL-GUARDAR (2026-09-12): borrador de los formularios de expediente (alta y edicion).
  // Guarda lo que se esta capturando para que no se pierda si la ventana se cierra o el
  // navegador se recarga. Un solo objeto: { "<usuarioId>:<expedienteId|nuevo>": {...} }.
  // INSTALAR-APP: decision de cada persona sobre la barra de "instala la app". Se guarda POR
  // USUARIO para que en un equipo compartido el "no, gracias" de uno no silencie el aviso al otro.
  INSTALAR_AVISO: 'beep_instalar_aviso',
  EXPEDIENTE_DRAFT: 'beep-exp-draft',

  // ========== MOCKS LEGACY ==========
  // Estos viven en localStorage porque sus modulos son mocks no conectados
  // a backend. Se eliminaran cuando se conecten al API real.
  MOCK_CLIENTES: 'beep_clientes',
  MOCK_TAREAS: 'beep_tareas',
  MOCK_USUARIOS: 'beep_usuarios',
  MOCK_PROYECTOS: 'beep_proyectos',
  MOCK_SERVICIOS: 'beep_servicios',
  MOCK_AGENDA_NOTAS: 'beep_agenda_notas',
  MOCK_AGENDA_PENDIENTES: 'beep_agenda_pendientes',
  MOCK_CANALES_NOLEIDOS: 'beep_canales_noleidos',

  // ========== OTRAS (constantes locales en modulos) ==========
  // Estos archivos definen su propia STORAGE_KEY constante. Se documentan
  // aqui para que el catalogo sea completo, pero NO se importan desde aqui
  // hasta que se haga el refactor de cada uno.
  //
  // - 'beep_clientes_nuevo_filtros'      ← ClientesNuevo.js
  // - 'beep_clientes_nuevo_token_previo' ← ClientesNuevo.js
  // - 'beep_expedientes_filtros'         ← Expedientes.js (ya migrada arriba como EXPEDIENTES_FILTROS, Expedientes.js usa literal por compat)
  // - 'beep_expedientes_token_previo'    ← Expedientes.js
  // - 'beep_tareas_personales'           ← AsistenteBeep.js
  // - 'beep_mapa_expo'                   ← MapaExpo.js
  // - 'beep_aprobaciones'                ← MiDia.js
  // - 'beep_aprobaciones_plan_<id>'      ← MiDia.js (dinamico)
  // - 'beep_tareas_dia'                  ← MiDia.js
  // - 'beep_pendientes_agenda'           ← MiAgenda.js
  // - 'beep_reporte_matutino'            ← ReporteMatutino.js
  // - 'beep_cliente_tienda'              ← components/SelectorCliente.js
  // - 'beep_moneda'                      ← components/SelectorMoneda.js
  // - 'beep_cotizaciones_pipeline'       ← components/PipelineCotizaciones.js (mock oculto)
  // - 'beep_almacen_*'                   ← Almacen.js
  // - 'beep_gastos'                      ← Gastos.js
  // - 'beep_rh_*'                        ← RecursosHumanos.js
  // - 'beep_centro_agentes_*'            ← CentroAgentes.js
  // - 'beep_dashboard_*'                 ← DashboardDirector.js
  // - 'beep_tareas_local'                ← Tareas.js
  // - 'beep_zona_stands'                 ← ZonaStands.js
};

/**
 * Helpers para leer y escribir con manejo de errores y JSON automatico.
 */
export const storage = {
  /**
   * Lee una clave. Si tiene JSON, parsea. Si no existe, devuelve null.
   */
  get(key) {
    try {
      const v = localStorage.getItem(key);
      if (v === null) return null;
      try { return JSON.parse(v); } catch { return v; }
    } catch (e) {
      console.error(`[storage.get] Error leyendo ${key}:`, e);
      return null;
    }
  },

  /**
   * Lee una clave SIN intentar parsear (devuelve string crudo o null).
   * Util para tokens, banderas tipo 'colapsado'/'expandido', etc.
   */
  getRaw(key) {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      console.error(`[storage.getRaw] Error leyendo ${key}:`, e);
      return null;
    }
  },

  /**
   * Escribe una clave. Stringifica objetos automaticamente. Strings van crudos.
   */
  set(key, value) {
    try {
      const v = typeof value === 'string' ? value : JSON.stringify(value);
      localStorage.setItem(key, v);
    } catch (e) {
      console.error(`[storage.set] Error escribiendo ${key}:`, e);
    }
  },

  /**
   * Borra una clave especifica.
   */
  remove(key) {
    try { localStorage.removeItem(key); } catch (e) {
      console.error(`[storage.remove] Error borrando ${key}:`, e);
    }
  },

  /**
   * Borra TODAS las claves del proyecto (logout). No toca claves de otras apps.
   * Reconoce ambos prefijos: 'beep_' y 'beep-'.
   */
  clearBeepKeys() {
    try {
      Object.keys(localStorage)
        .filter(k => k.startsWith('beep_') || k.startsWith('beep-'))
        .forEach(k => localStorage.removeItem(k));
    } catch (e) {
      console.error('[storage.clearBeepKeys] Error:', e);
    }
  },

  /**
   * Limpia solo claves de navegacion (al cambiar de app). Preserva auth y preferencias.
   */
  clearNavigation() {
    this.remove(STORAGE_KEYS.DASHBOARD_ACTIVE_TAB);
  },
};

/**
 * SEGURIDAD F1.B: header Authorization canonico para fetch.
 * Uso: fetch(url, { method: 'DELETE', headers: { ...authHeaders() } })
 * Devuelve {} si no hay token (el backend respondera 401).
 */
export function authHeaders() {
  const t = storage.getRaw(STORAGE_KEYS.AUTH_TOKEN);
  return t ? { Authorization: `Bearer ${t}` } : {};
}

// Para debugging desde la consola del navegador:
//   window.__beep_storage.get('beep_token')
//   window.__beep_storage.clearBeepKeys()
if (typeof window !== 'undefined') {
  window.__beep_storage = storage;
}
